import React from 'react';

import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import MerchandisingProgramPrices from './MerchandisingProgramPrices';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import { usePatchMerchandisingProgramQuoteChangeInformation } from 'services/Data/MerchandisingProgramQuoteService';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import CustomDatePicker from 'Components/CustomDatePicker/CustomDatePicker';
import moment from 'moment';
import { useDealer } from 'services/Data/DealerServices';
import { useMerchandisingProgramCategories } from 'services/Data/MerchandisingProgramCategoryService';
import { getMerchandisingCategoryKind } from 'services/Utilities/categoryHelpers';

const MerchandisingProgramQuoteConsentments = ({
  isDisabled,
  setConsentmentAccepted,
  consentmentAccepted,
  merchandisingProgramQuote,
  validator,
}) => {
  const { t } = useTranslation();

  const {
    mutate: changeInformations,
    isPending: changingInformations,
  } = usePatchMerchandisingProgramQuoteChangeInformation();

  const { data: dealer, isFetching: gettingDealer } = useDealer();
  const { data: merchandisingProgramCategories } = useMerchandisingProgramCategories();

  const merchandisingProgramCategoriesNoTraining =
    (merchandisingProgramCategories || []).filter(category => getMerchandisingCategoryKind(category) !== 'trainStaff') || [];

  return (
    <Box width={'100%'}>
      <Typography variant="h1">
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentTitle') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentByBetween') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentByDealer') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentByParties') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentIntroductionTitle') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentIntroductionInfo') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentIntroductionAgree') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_1') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_1_1') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_1_2') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_2') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_2_1') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_2_2') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_2_3') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_2_4') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_2_5') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_1') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_1_body1') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3, ml: 5 }}>
        <ul>
          <li>
            <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_1_bullet1') }} />
          </li>
        </ul>
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_2') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_2_body1') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3, ml: 5 }}>
        <ul>
          <li>
            <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_2_bullet1') }} />
          </li>
        </ul>
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_3') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_3_body1') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3, ml: 5 }}>
        <ul>
          <li>
            <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_3_bullet1') }} />
          </li>
          <li>
            <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_3_bullet2') }} />
          </li>
        </ul>
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_4') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_4_body1') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3, ml: 5 }}>
        <ul>
          <li>
            <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_4_bullet1') }} />
          </li>
          <Typography variant="h5" sx={{ mt: 3, ml: 5 }}>
            <ul>
              <li>
                <span
                  dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_4_bullet1_nested1') }}
                />
              </li>
              <li>
                <span
                  dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_4_bullet1_nested2') }}
                />
              </li>
              <li>
                <span
                  dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_3_4_bullet1_nested3') }}
                />
              </li>
            </ul>
          </Typography>
        </ul>
      </Typography>

      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_4') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_4_1') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_4_1_body1') }} />
      </Typography>

      <Typography sx={{ mt: 3 }}>
        <MerchandisingProgramPrices
          fullWidth={isDisabled}
          merchandisingProgramCategoriesToUse={merchandisingProgramCategoriesNoTraining}
        />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_4_1_body2') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_4_2') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_4_2_body1') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_5') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_5_1') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_5_2') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_5_3') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_5_4') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_5_5') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 5 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_6') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_6_1') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_6_2') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_6_3') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_6_4') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPart_6_5') }} />
      </Typography>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementFooter') }} />
      </Typography>

      <Box sx={{ my: 5 }}>
        <FormControlLabel
          sx={{ mb: 3 }}
          control={
            <Checkbox
              onChange={({ target: { checked } }) => setConsentmentAccepted(checked)}
              checked={consentmentAccepted || false}
              disabled={isDisabled}
            />
          }
          label={t('merchandisingProgramConsentmentAgreementAcceptConsentment')}
        />

        <GridContainer>
          <GridItem xs={12} md={4}>
            <SkeletonWrapper
              skeletonProps={{
                width: 210,
                height: 33,
              }}
              isLoading={changingInformations}
              component={
                merchandisingProgramQuote && (
                  <CustomInput
                    labelText={t('fullName')}
                    formControlProps={{
                      fullWidth: true,
                    }}
                    error={validator.current.message(t('fullName'), merchandisingProgramQuote?.signature, `required`)}
                    inputProps={{
                      onBlur: ({ target: { value } }) => {
                        changeInformations({ ...merchandisingProgramQuote, signature: value });
                      },
                      disabled: isDisabled || !consentmentAccepted,
                      defaultValue: merchandisingProgramQuote.signature || '',
                    }}
                  />
                )
              }
            />
          </GridItem>

          <GridItem xs={12} md={4}>
            <SkeletonWrapper
              skeletonProps={{
                width: 210,
                height: 33,
              }}
              isLoading={changingInformations || gettingDealer}
              component={
                merchandisingProgramQuote && (
                  <CustomInput
                    labelText={t('dealer')}
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      disabled: true,
                      defaultValue: dealer?.customerName || '',
                    }}
                  />
                )
              }
            />
          </GridItem>
          <GridItem xs={12} md={4}>
            <SkeletonWrapper
              skeletonProps={{
                width: 210,
                height: 33,
              }}
              isLoading={changingInformations}
              component={
                merchandisingProgramQuote && (
                  <CustomDatePicker
                    labelText={t('date')}
                    disableFuture={true}
                    dateFormat={t('date_format_with_time')}
                    formControlProps={{ fullWidth: true }}
                    error={validator.current.message(t('date'), merchandisingProgramQuote?.signatureDate, `required`)}
                    datePickerProps={{
                      value: merchandisingProgramQuote?.signatureDate,
                      disabled: isDisabled || !consentmentAccepted,
                      onChange: value => {
                        const momentValueDate = moment(value);
                        const momentCurrentDate = merchandisingProgramQuote?.signatureDate
                          ? moment(merchandisingProgramQuote?.signatureDate)
                          : null;

                        if (momentValueDate !== momentCurrentDate) {
                          changeInformations({ ...merchandisingProgramQuote, signatureDate: momentValueDate.toDate() });
                        }
                      },
                    }}
                  />
                )
              }
            />
          </GridItem>
        </GridContainer>
      </Box>

      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPrivacyPolicy') }} />
      </Typography>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <span dangerouslySetInnerHTML={{ __html: t('merchandisingProgramConsentmentAgreementPrivacyPolicy_body1') }} />
      </Typography>
    </Box>
  );
};

export default MerchandisingProgramQuoteConsentments;
